import { useState, useRef } from 'react'
import PropTypes from 'prop-types'

import styled from '@emotion/styled'

import VideoThumb from '../VideoThumb'
import VideoPlayer from '../VideoPlayer'
import useOnScreen from '../../../ui/hooks/useOnScreen'
import handleVideoErrors from '../../../../helpers/handleVideoErrors'

const StyledThumb = styled.div(({ show, theme: { breakpoints, mediaSizes } }) => ({
  display: show ? 'flex' : 'none',
  justifyContent: 'center',
  width: '100%',
  maxHeight: mediaSizes.mobile.maxHeight,
  maxWidth: '100%',
  [`@media (min-width: ${breakpoints.medium})`]: {
    maxHeight: mediaSizes.desktop.maxHeight,
    position: 'static',
  },
}))

const StyledVideo = styled.div(({ show, theme: { breakpoints } }) => ({
  backgroundColor: 'black',
  display: show ? 'flex' : 'none',
  justifyContent: 'center',
  maxWidth: '100%',
  width: '100%',
  [`@media (min-width: ${breakpoints.medium})`]: {
    position: 'static',
  },
}))

const MediaVideo = ({
  theme,
  src,
  containerRef,
  previewSrc,
  lazy,
  isAutoPlayEnabled,
  onVideoStart,
  onVideoStep,
  onVideoEnterFullscreen,
}) => {
  const [firstPlay, setFirstPlay] = useState(isAutoPlayEnabled)
  const videoRef = useRef()

  useOnScreen({
    target: { current: videoRef.current?.wrapper?.firstElementChild },
    onExitScreen: () => {
      videoRef.current.wrapper.firstElementChild.pause()
    },
  }, { root: containerRef })

  const percSteps = [20, 40, 60, 80, 100]

  const percStepsEvents = percSteps.reduce((events, step) => ({
    ...events,
    [step]: false,
  }), {})

  const onPlayerProgress = (playerRef, seeked) => {
    const { current: player } = playerRef
    const currentTime = player.getCurrentTime()
    const duration = player.getDuration()
    const perc = (currentTime / duration) * 100

    for (const percStep of percSteps) {
      if (perc >= percStep && !percStepsEvents[percStep]) {
        percStepsEvents[percStep] = true
        onVideoStep({
          step: percStep,
          seeked,
        })
      }
    }
  }

  const onFirstPlayButtonClick = async () => {
    setFirstPlay(true)
    try {
      await videoRef.current.wrapper.firstElementChild.play()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  const onVideoError = event => handleVideoErrors(event, src)

  return (
    <>
      <StyledThumb show={!firstPlay}>
        <VideoThumb
          src={previewSrc}
          lazy={lazy}
          onClick={onFirstPlayButtonClick}
        />
      </StyledThumb>
      <StyledVideo show={firstPlay}>
        <VideoPlayer
          onVideoEnterFullscreen={onVideoEnterFullscreen}
          onPlayerProgress={onPlayerProgress}
          onVideoStart={onVideoStart}
          onVideoError={onVideoError}
          src={src}
          isAutoPlayEnabled={isAutoPlayEnabled}
          width="100%"
          height="100%"
          videoRef={videoRef}
          theme={theme}
        />
      </StyledVideo>
    </>
  )
}

MediaVideo.propTypes = {
  theme: PropTypes.shape({}).isRequired,
  src: PropTypes.string,
  containerRef: PropTypes.shape({}),
  previewSrc: PropTypes.string,
  lazy: PropTypes.bool,
  isAutoPlayEnabled: PropTypes.bool,
  onVideoStart: PropTypes.func,
  onVideoStep: PropTypes.func,
  onVideoEnterFullscreen: PropTypes.func,
}

MediaVideo.defaultProps = {
  src: '',
  previewSrc: '',
  containerRef: null,
  lazy: true,
  isAutoPlayEnabled: false,
  onVideoStart: () => false,
  onVideoStep: () => false,
  onVideoEnterFullscreen: () => false,
}

MediaVideo.displayName = 'MediaVideo'
MediaVideo.whyDidYouRender = true

export default MediaVideo
